import { AutosaveTourBanner } from '@/components/Patient/components/AutosaveTourBanner';
import {
  getOrderedFormsByRole,
  renderAddressForm,
  renderConcessionCardForm,
  renderContactDetailsForm,
  renderEmergencyContactForm,
  renderIhiForm,
  renderMedicareForm,
  renderPatientInformationForm,
  renderTreatingDoctorForm,
} from '@/components/Patient/components/PatientDetailsTabContent';
import type { PatientDetailsContent } from '@/components/Patient/hooks/usePatientDetailsContent';
import type { PatientDetailsDataState } from '@/components/Patient/hooks/usePatientDetailsData';
import { FF_PATIENT_PAGE_AUTOSAVE_TOUR } from '@/constants/featureFlags';
import type { Role } from '@/data/service/authService';
import { useFeatureFlags } from '@/hooks';
import type { ConcessionCardData } from '@/hooks/patients/useGetPatientConcessionCard';
import type { PatientDetailsData } from '@/hooks/patients/useGetPatientDetails';
import { PatientDetailsIncludeEnum, useGetPatientDetails } from '@/hooks/patients/useGetPatientDetails';
import { usePatientPhoneNumber } from '@/hooks/usePatientPhoneNumber';
import { useAppStore } from '@/state-management';
import type { ATSI } from '@montugroup/pms-api-contracts';
import Grid from '@mui/material/Grid';
import { useEffect } from 'react';
import type { FieldValues } from 'react-hook-form';
import { shallow } from 'zustand/shallow';
import { ContactPatient } from './ContactPatient';
import type { AddressFormFields } from './forms/AddressForm/AddressForm';
import type { ContactDetailsFormFields } from './forms/ContactDetails/ContactDetails';
import type { IhiInformationFormFields } from './forms/IhiInformation/IhiInformation.schema';
import type { PatientInformationFormFields, ResendConsentData } from './forms/PatientInformation/PatientInformation';
import type { TreatingDoctorFormFields } from './forms/TreatingDoctor/TreatingDoctor';
import { usePatientIhi } from './hooks/usePatientIhi';

export interface PatientDetailsTabProps {
  setData: (data: PatientDetailsDataState) => void;
  setContents: (contents: PatientDetailsContent) => void;
  data: PatientDetailsDataState;
  patientDetailsData?: PatientDetailsData;
  contents: PatientDetailsContent;
  arePatientFormsEditable: boolean;
  isLoading: boolean;
  concessionCardData?: ConcessionCardData | null;
  role: Role | null;
}

const PatientDetailsTab = ({
  setData,
  setContents,
  data,
  contents,
  arePatientFormsEditable,
  concessionCardData,
  role,
}: PatientDetailsTabProps) => {
  const { flags } = useFeatureFlags();
  const ffAutosaveTour = flags[FF_PATIENT_PAGE_AUTOSAVE_TOUR];
  const { verifyIhi } = usePatientIhi();
  const { shouldDisplayPatientPhoneNumber, maskPhoneNumber } = usePatientPhoneNumber();
  const patientId = useAppStore.use.getPatientId()();
  const setPatientBasicInfo = useAppStore.use.setPatientBasicInfo();
  const setPatientIdentificationInfo = useAppStore.use.setPatientIdentificationInfo();
  const setPatientContactDetails = useAppStore.use.setPatientContactDetails();

  const endpointsString = [
    PatientDetailsIncludeEnum.BasicInfo,
    PatientDetailsIncludeEnum.CareInfo,
    PatientDetailsIncludeEnum.EmergencyContact,
    PatientDetailsIncludeEnum.IdentificationInfo,
    PatientDetailsIncludeEnum.ContactDetails,
  ].join(',');

  const { result: patientDetailsResult, isLoading } = useGetPatientDetails(patientId as number, endpointsString);

  const patientDetailsData = patientDetailsResult.data?.body?.data;
  const patientBasicInfo = patientDetailsData?.basicInfo?.attributes;
  const patientIdentificationInfo = patientDetailsData?.identificationInfo?.attributes;
  const patientEmergencyContact = patientDetailsData?.emergencyContact?.attributes;
  const patientCareInfo = patientDetailsData?.careInfo?.attributes;
  const patientContactDetails = patientDetailsData?.contactDetails?.attributes;

  /**
   * hydrate the patient slice in store
   */
  useEffect(() => {
    setPatientBasicInfo({
      firstName: patientBasicInfo?.firstName,
      lastName: patientBasicInfo?.lastName,
      dob: patientBasicInfo?.dob,
      gender: patientBasicInfo?.gender,
      stateCode: patientBasicInfo?.residentialAddress?.state?.name,
    });
    setPatientIdentificationInfo({
      ihiNumber: patientIdentificationInfo?.ihiNumber,
      ihiNumberStatus: patientIdentificationInfo?.ihiNumberStatus,
      ihiRecordStatus: patientIdentificationInfo?.ihiRecordStatus,
      medicareExpDate: patientIdentificationInfo?.medicareExpDate,
      medicareNumber: patientIdentificationInfo?.medicareNumber,
      medicareRefNumber: patientIdentificationInfo?.medicareRefNumber,
    });
    setPatientContactDetails({
      email: patientContactDetails?.email,
      phone: patientContactDetails?.phone,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientDetailsData, setPatientBasicInfo, setPatientContactDetails, setPatientIdentificationInfo]);

  // This subscripton will trigger a verifyIHI
  useEffect(() => {
    const unsub = useAppStore.subscribe(
      (state) => state.patient.needsIhiVerification,
      (needsIhiVerification) => {
        if (needsIhiVerification) {
          verifyIhi();
        }
      },
      {
        equalityFn: shallow,
      },
    );
    return unsub;
  }, [verifyIhi]);

  const handleIhiInformationSave = async (ihiInformation: IhiInformationFormFields) => {
    setData({
      ...data,
      ihi_number: ihiInformation.ihiNumber ?? null,
    });
  };

  const handleMedicareInformationSave = async (medicareInformation: FieldValues) => {
    setData({
      ...data,
      medicareNumber: medicareInformation.medicareNumber,
      medicare_ref_number: medicareInformation.medicareRefNumber,
      medicareExpiration: medicareInformation.medicareExpDate,
    });
  };

  const handlePatientInformationSave = async (patientInformation: PatientInformationFormFields) => {
    setData({
      ...data,
      FirstName: patientInformation.firstName ?? null,
      LastName: patientInformation.lastName ?? null,
      DoB: patientInformation.dob ?? null,
      consent: patientInformation.consent ?? null,
      gender: patientInformation.gender ?? null,
      atsi_status: patientInformation.atsiStatus ?? null,
      pmsDoctor: patientInformation.doctorId ?? null,
      dvaNumber: patientInformation.dvaNumber ?? null,
    });
  };

  const handleContactDetailsSave = async (contactDetails: ContactDetailsFormFields) => {
    setData({ ...data, ...contactDetails });
  };

  const handleConcessionCardSave = async () => {
    // Void method as we don't handle saving in this form
  };

  const handleAddressSave = async (addressData: AddressFormFields) => {
    setData({
      ...data,
      street: {
        line1: addressData.streetLine1,
        line2: addressData.streetLine2 ?? null,
      },
      suburb: addressData.city,
      postcode: addressData.zipCode,
      state: (addressData.state as number) ?? null,
    });
  };

  const handleTreatingDoctorSave = async (treatingDoctor: TreatingDoctorFormFields) => {
    setData({
      ...data,
      doctorClinic: treatingDoctor.gpClinic ?? null,
      doctorName: treatingDoctor.gpName ?? null,
      doctorEmail: treatingDoctor.gpEmail ?? null,
      doctorPhone: treatingDoctor.gpPhone ?? null,
    });
  };

  const handleOnResendConsent = async (resendConsent: ResendConsentData) => {
    setContents({ ...contents, ...resendConsent });
  };

  const patientDetailsTabContent = getOrderedFormsByRole(role, {
    medicareForm: renderMedicareForm({
      defaultValues: {
        medicareNumber: patientIdentificationInfo?.medicareNumber ?? '',
        medicareExpDate: patientIdentificationInfo?.medicareExpDate ?? '',
        medicareRefNumber: patientIdentificationInfo?.medicareRefNumber ?? '',
      },
      editable: arePatientFormsEditable,
      onSubmit: handleMedicareInformationSave,
      isLoading: isLoading,
    }),
    ihiForm: renderIhiForm({
      defaultValues: {
        ihiNumber: patientIdentificationInfo?.ihiNumber,
        ihiNumberStatus: patientIdentificationInfo?.ihiNumberStatus,
        ihiRecordStatus: patientIdentificationInfo?.ihiRecordStatus,
      },
      onSubmit: handleIhiInformationSave,
      editable: arePatientFormsEditable,
      isLoading: isLoading,
    }),
    patientInformationForm: renderPatientInformationForm({
      defaultValues: {
        firstName: patientBasicInfo?.firstName,
        lastName: patientBasicInfo?.lastName,
        dob: patientBasicInfo?.dob,
        consent: Boolean(data.consent),
        gender: patientBasicInfo?.gender,
        atsiStatus: patientBasicInfo?.atsiStatus as ATSI,
        doctorId: patientBasicInfo?.doctorId,
        dvaNumber: patientIdentificationInfo?.dvaNumber,
      },
      showConsentFormResendLink: contents.showConsentFormResendLink,
      onSubmit: handlePatientInformationSave,
      onResendConsent: handleOnResendConsent,
      editable: arePatientFormsEditable,
      isLoading: isLoading,
    }),
    contactDetailsForm: renderContactDetailsForm({
      defaultValues: {
        email: patientContactDetails?.email,
        phone: patientContactDetails?.phone,
      },
      isEmailEditable: role === 'admin' || role === 'nurse' || role === 'manager',
      maskedPhoneNumber: shouldDisplayPatientPhoneNumber
        ? undefined
        : maskPhoneNumber(patientContactDetails?.phone ?? ''),
      onSubmit: handleContactDetailsSave,
      editable: arePatientFormsEditable,
      isLoading: isLoading,
    }),
    emergencyContactForm: renderEmergencyContactForm({
      defaultValues: {
        firstName: patientEmergencyContact?.firstName,
        lastName: patientEmergencyContact?.lastName,
        relationship: patientEmergencyContact?.relationship,
        phoneNumber: patientEmergencyContact?.phoneNumber,
      },
      editable: arePatientFormsEditable,
      isLoading: isLoading,
    }),
    addressForm: renderAddressForm({
      defaultValues: {
        streetLine1: patientBasicInfo?.residentialAddress?.address?.split('$$$$')[0] ?? '',
        streetLine2: patientBasicInfo?.residentialAddress?.address?.split('$$$$')[1] ?? '',
        city: patientBasicInfo?.residentialAddress?.city ?? '',
        zipCode: patientBasicInfo?.residentialAddress?.zipCode ?? '',
        state: patientBasicInfo?.residentialAddress?.state
          ? {
              id: patientBasicInfo?.residentialAddress?.state.id,
              label: patientBasicInfo?.residentialAddress?.state?.name,
            }
          : 0,
      },
      onSubmit: handleAddressSave,
      editable: arePatientFormsEditable,
      isLoading: isLoading,
    }),
    treatingDoctorForm: renderTreatingDoctorForm({
      defaultValues: {
        gpName: patientCareInfo?.gpName,
        gpEmail: patientCareInfo?.gpEmail,
        gpPhone: patientCareInfo?.gpPhone,
        gpClinic: patientCareInfo?.gpClinic,
      },
      onSubmit: handleTreatingDoctorSave,
      editable: arePatientFormsEditable,
      isLoading: isLoading,
    }),
    concessionCardForm: renderConcessionCardForm({
      hasConcessionCard: Boolean(concessionCardData),
      defaultValues: {
        cardNumber: concessionCardData?.cardNumber ?? undefined,
        cardType: concessionCardData?.cardType ?? undefined,
        expiryDay: concessionCardData?.expiryDay ?? undefined,
        expiryMonth: concessionCardData?.expiryMonth ?? undefined,
        expiryYear: concessionCardData?.expiryYear ?? undefined,
        hasValidConcessionCard: contents.patient.hasValidConcessionCard,
      },
      onSubmit: handleConcessionCardSave,
      isLoading: isLoading,
    }),
  });

  return (
    <Grid container spacing={6} data-testid="patient-details-tab">
      <ContactPatient
        patientId={String(patientId)}
        patientPhoneNumber={patientContactDetails?.phone ?? ''}
        patientName={`${patientBasicInfo?.firstName ?? ''} ${patientBasicInfo?.lastName ?? ''}`}
      />
      {ffAutosaveTour && <AutosaveTourBanner />}
      {patientDetailsTabContent}
    </Grid>
  );
};
export default PatientDetailsTab;
